import { 
  AfterViewInit,
  ChangeDetectionStrategy, Component, ElementRef, inject, Input, OnDestroy, OnInit, QueryList, ViewChild,
  ViewChildren 
} from '@angular/core';
import { AsyncPipe, CommonModule, KeyValuePipe } from '@angular/common';
import { TranslateModule, TranslateService, TranslateStore } from '@ngx-translate/core';
import { TextareaWithChipsComponent } from '../textarea-with-chips/textarea-with-chips.component';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ValidationMessageComponent } from '../validation-message/validation-message.component';
import { 
  BehaviorSubject, catchError, combineLatest, map, merge, Observable, of, Subject, take, takeUntil, 
  throttleTime, timer 
} from 'rxjs';
import { InformationTemplate, PreviewEditForm, TrafficDeviationsStore } from '@components/traffic-deviations/stores/traffic-deviations.store';
import { InformationDetailsComponent } from '../information-details/information-details.component';
import { dirtyCheck } from '@modules/core/operators/dirty-check.operator';
import { isEqual } from 'lodash';
import { Guid } from '@modules/shared/helpers/guid-helper';
import { TaService } from '@modules/core/services/ta.service';
import { TrafficDeviationInformationTemplateRequest } from '@modules/shared/interfaces/traffic-deviation-information-template/traffic-deviation-information-template-request';
import { handleError } from '@modules/core/services/service.helpers';
import { AuthService } from '@modules/core/services';
import { ConfirmationDialogData } from '@modules/shared/dialogs';
import { DialogService } from '@modules/shared/services/dialog.service';

@Component({
    selector: 'app-edit-templates',
    imports: [
        CommonModule, AsyncPipe, KeyValuePipe, TranslateModule, ReactiveFormsModule,
        TextareaWithChipsComponent, ValidationMessageComponent, InformationDetailsComponent
    ],
    templateUrl: './edit-templates.component.html',
    styleUrls: ['./edit-templates.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditTemplatesComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() selectedTemplate$: BehaviorSubject<InformationTemplate>;

  @ViewChild('previewFormName') previewFormName: ElementRef<HTMLInputElement>;
  @ViewChild('addingChip') addingChipElement: ElementRef<HTMLInputElement>;
  @ViewChildren('textarea') textareas: QueryList<TextareaWithChipsComponent>;

  public selectedTemplateId = '';

  private readonly _formBuilder = inject(NonNullableFormBuilder);
  private readonly _trafficDeviationsStore = inject(TrafficDeviationsStore);
  private readonly _taService = inject(TaService);
  private readonly _authService = inject(AuthService);
  private readonly _translateService = inject(TranslateService);
  private readonly _translateStore = inject(TranslateStore);
  private readonly _dialogService = inject(DialogService);

  protected templateMessageBackgroundCritical = false;
  protected nameReadOnly$ = new BehaviorSubject(true);
  protected templateMessage$ = new BehaviorSubject('');
  protected selectedTextareaToRecieveChip$ = new BehaviorSubject('');
  protected isDirty$: Observable<boolean>;

  protected readonly requiredCustomControls$ = new BehaviorSubject<string[]>([]);
  protected readonly previewForm = this._formBuilder.group({
    id: ['', [Validators.required, Validators.maxLength(50)]],
    version: [0, [Validators.required]],
    name: ['', [Validators.required, Validators.maxLength(50)]],
    summary: ['', [Validators.required, Validators.maxLength(3000)]],
    header: ['', [Validators.required, Validators.maxLength(3000)]],
    content: ['', [Validators.required, Validators.maxLength(3000)]],
    deviationTypes: this._formBuilder.group({
      DeviationInformation: false,
      ChangeOfJourneyPattern: false,
      ChangeOfJourneyTiming: false,
      PredictionInaccurate: false
    }),
    modifiedDate: ['', [Validators.maxLength(50)]],
    modifiedBy: ['', [Validators.required, Validators.maxLength(50)]],
    automaticTemplate: [false],
    automaticDescription: ['', [Validators.maxLength(50)]],
    addingChip: [{ value: '', disabled: true }]
  });

  protected readonly automaticVariables$ = combineLatest([
    this._trafficDeviationsStore.automaticRouteInfoVariable$,
    this._trafficDeviationsStore.automaticVariables$
  ]).pipe(
    map(([arr1, arr2]) => [...arr1, ...arr2].sort((a, b) => {
      const translatedA: string = this._translateService.instant(a);
      const translatedB: string = this._translateService.instant(b);
      
      return translatedA.localeCompare(translatedB);
    }))
  );

  private readonly _manuelVariables$ = new BehaviorSubject<Array<string>>([]);
  protected readonly manuelVariables$ = combineLatest([
    this._trafficDeviationsStore.manualVariables$,
    this._manuelVariables$
  ]).pipe(
    map(([v1, v2]) => [...v1, ...v2].sort((a, b) => {
      const translatedA: string = this._translateService.instant(a);
      const translatedB: string = this._translateService.instant(b);
      
      return translatedA.localeCompare(translatedB);
    }))
  );

  private readonly _destroy$ = new Subject<void>();
  private readonly _unchangedForm$ = 
    new BehaviorSubject<Omit<PreviewEditForm, 'unchangedForm'>>(undefined);

  ngOnInit(): void {
    this._setupSubscriptionToSelectedTemplate();
  }

  ngAfterViewInit(): void {
    this._setupLoadManualVariablesTrigger();
    this._onTemplateChangeSetModifiedBy();
  }

  ngOnDestroy(): void {
    if (this.checkIsDirty()) {
      // if previewForm is dirty we have unsaved changed to a template
      // save this state and load next time the user opens the edit template view
      this._trafficDeviationsStore.updateTrafficDeviationState({ 
        savedPreviewEditForm: {
          ...this.previewForm.getRawValue(),
          unchangedForm: this._unchangedForm$.getValue()
        }
      });
    }

    this._destroy$.next();
    this._destroy$.complete();
  }

  /** Check if the open template have had any changes to it */
  public checkIsDirty(): boolean {
    const unchangedForm = this._unchangedForm$.getValue();
    if (unchangedForm && !isEqual(unchangedForm, this.previewForm.getRawValue())) {
      return true;
    }

    return false;
  }

  public publishTemplate(): Observable<boolean> {
    if (!this.previewForm.valid) { 
      const message = this._translateService.instant('The form is not valid');
      this._publishTemplateMessage(message, true);

      return of(false);
    }

    const informationTemplate = this._mapToRequestObject();
    const responseObservable: Subject<boolean> = new Subject();

    this._trafficDeviationsStore.informationTemplates$.pipe(
      take(1)
    ).subscribe((informationTemplates) => {
      const templateIds = informationTemplates.map((template) => template.id);
      if (templateIds.includes(this.selectedTemplateId)) {
        informationTemplate.version += 1;

        this._taService.putTrafficDeviationInformationTemplates(informationTemplate).pipe(
          catchError((error) => {
            responseObservable.next(false);
            return handleError(error);
          })
        ).subscribe(() => {
          const translation = this._translateService.instant('Template: {{name}}, edited', { 
            name: informationTemplate.name 
          });

          this._publishTemplateMessage(translation);
          this._getTrafficDeviationInformationTemplates();
          responseObservable.next(true);
        });
      } else {
        this._taService.postTrafficDeviationInformationTemplates(informationTemplate).pipe(
          catchError((error) => {
            responseObservable.next(false);
            return handleError(error);
          })
        ).subscribe(() => {
          const translation = this._translateService.instant('New template: {{name}}, published', { 
            name: informationTemplate.name 
          });
          
          this._publishTemplateMessage(translation);
          this._getTrafficDeviationInformationTemplates();
          responseObservable.next(true);
        });
      }
    });
 
    return responseObservable.asObservable();
  }

  protected onSubmit(): Observable<boolean> {
    if (!this.previewForm.valid) return of(false);

    const modalData = new ConfirmationDialogData(
      this._translateService.instant('Warning'),
      this._translateService.instant('If you continue, you will update the template'),
      this._translateService.instant('Continue'),
      this._translateService.instant('Cancel')
    );
    const response: Subject<boolean> = new Subject();
    
    this._dialogService.showConfirmation(modalData).pipe(take(1)).subscribe((reponse) => {
      if (reponse) {
        this.publishTemplate().pipe(take(1)).subscribe((responseValue) => {
          response.next(responseValue);
        });
        return;
      }

      response.next(false);
    });

    return response.asObservable();
  }

  protected deleteTemplate(): void {
    if (!this.selectedTemplateId) return;

    let informationTemplates: InformationTemplate[] = [];
    this._trafficDeviationsStore.informationTemplates$.pipe(
      take(1)
    ).subscribe((templates) => informationTemplates = templates);

    if (!informationTemplates?.length) {
      this.selectedTemplate$.next({} as InformationTemplate);
      return;
    }

    const modalData = new ConfirmationDialogData(
      this._translateService.instant('Warning'),
      this._translateService.instant('If you continue, you will DELETE the template'),
      this._translateService.instant('DELETE'),
      this._translateService.instant('Cancel')
    );
    
    this._dialogService.showConfirmation(modalData).pipe(take(1)).subscribe((reponse) => {
      if (reponse) this._deleteTemplate();
    });
  }

  protected onFocusSelectedTextarea(textareaControlName: string): void {
    this.selectedTextareaToRecieveChip$.next(textareaControlName);
  }

  protected onBlurEditName(): void {
    if (!this.previewForm.controls.name.disabled) {
      timer(100).subscribe(() => {
        this.disableNameEdit();
      });
    }
  }

  protected toggleAddingChip(): void {
    this.selectedTextareaToRecieveChip$.pipe(
      take(1)
    ).subscribe((selectedTextarea) => {
      if (selectedTextarea) {
        if (this.previewForm.controls.addingChip.enabled) {
          this.onAddingChip(this.previewForm.controls.addingChip.value);
          return;
        }
        
        this.previewForm.controls.addingChip.enable();
        timer(100).subscribe(() => {
          this.addingChipElement.nativeElement.focus();
        });
      }
    });
  }

  protected enableNameEdit(): void {
    this.nameReadOnly$.next(false);
    this.previewFormName.nativeElement.focus();
  }

  protected disableNameEdit(): void {
    this.nameReadOnly$.next(true);
  }

  protected onAddingChip(chip: string): void {
    if (this.previewForm.controls.addingChip.disabled) return;
    if (chip) {
      this.addChipToSelectedTextarea(chip);
    }

    this.previewForm.controls.addingChip.setValue('');
    this.previewForm.controls.addingChip.disable();
  }

  protected addChipToSelectedTextarea(chip: string): void {
    const selectedTextareaToRecieveChip = this.selectedTextareaToRecieveChip$.getValue();
    if (!selectedTextareaToRecieveChip) return;

    for (const textarea of this.textareas) {
      if (textarea.options.id === selectedTextareaToRecieveChip) {
        textarea.insertElementAtCursorPosition(chip);
        break;
      }
    }
  }

  private _changeTemplate(selectedTemplate: InformationTemplate): void {
    if (selectedTemplate) {
      if (!selectedTemplate.id) return;
      if (this.selectedTemplateId === selectedTemplate.id) return;

      this.selectedTemplateId = selectedTemplate.id;
      this.previewForm.patchValue({
        id: selectedTemplate.id,
        version: selectedTemplate.version,
        name: selectedTemplate.name,
        summary: selectedTemplate.summary,
        header: selectedTemplate.header,
        content: selectedTemplate.content,
        deviationTypes: {
          DeviationInformation: selectedTemplate.deviationTypes.includes('DeviationInformation'),
          ChangeOfJourneyPattern: selectedTemplate.deviationTypes.includes('ChangeOfJourneyPattern'),
          ChangeOfJourneyTiming: selectedTemplate.deviationTypes.includes('ChangeOfJourneyTiming'),
          PredictionInaccurate: selectedTemplate.deviationTypes.includes('PredictionInaccurate')
        },
        modifiedBy: selectedTemplate.modifiedBy,
        modifiedDate: selectedTemplate.modifiedDate,
        automaticTemplate: selectedTemplate.automaticTemplate,
        automaticDescription: selectedTemplate.automaticDescription
      });

      this.previewFormName.nativeElement.dispatchEvent(new Event('input'));

      this._setIsDirty();

      this._trafficDeviationsStore.savedPreviewEditForm$.pipe(take(1)).subscribe((savedForm) => {
        if (savedForm) {
          this._unchangedForm$.next(savedForm.unchangedForm);    
          this._trafficDeviationsStore.updateTrafficDeviationState({ 
            savedPreviewEditForm: undefined 
          });
        }
      });

      this._onTemplateChangeSetModifiedBy();
    }
  }

  private _createNewTemplate(): void {
    this.selectedTemplateId = Guid.newGuid();
    this.previewForm.patchValue({
      id: this.selectedTemplateId,
      version: 1,
      name: '',
      summary: '',
      header: '',
      content: '',
      deviationTypes: {
        DeviationInformation: true,
        ChangeOfJourneyPattern: true,
        ChangeOfJourneyTiming: true,
        PredictionInaccurate: true
      },
      modifiedBy: this._authService.user?.fullName,
      modifiedDate: '',
      automaticTemplate: false,
      automaticDescription: ''
    });

    this.previewFormName.nativeElement.dispatchEvent(new Event('input'));
    
    this._setIsDirty();
    this.enableNameEdit();
  }

  private _setIsDirty(): void {
    this._unchangedForm$.next(structuredClone(this.previewForm.getRawValue()));
    
    this.isDirty$ = this.previewForm.valueChanges.pipe(
      map(() => {
 return this.previewForm.getRawValue(); 
}),
      dirtyCheck(this._unchangedForm$.asObservable()),
      takeUntil(this._destroy$)
    );
  }

  private _loadManualVariables(usedVariables: Set<string>): void {
    this._manuelVariables$.next([]);
    let staticManualVariables: string[] = [];

    this._trafficDeviationsStore.manualVariables$.pipe(take(1)).subscribe((manualVariables) => {
      if (manualVariables) {
        staticManualVariables = [...manualVariables];
      }
    });

    this.automaticVariables$.pipe(
      take(1),
    ).subscribe((automaticVariables) => {
      if (!automaticVariables?.length) return;

      const manualVariables = [...this._manuelVariables$.getValue()];
      let requiredCustomControls = [];

      for (const usedVariable of usedVariables) {
        if (automaticVariables.includes(usedVariable)) continue;

        if (!requiredCustomControls.includes(usedVariable)) {
          requiredCustomControls.push(usedVariable);
        }
        if (staticManualVariables.includes(usedVariable)) continue;

        manualVariables.push(usedVariable);
      }

      this.requiredCustomControls$.next(requiredCustomControls);
      this._manuelVariables$.next(manualVariables);
    });
  }

  private _setupSubscriptionToSelectedTemplate(): void {
    this.selectedTemplate$.pipe(
      throttleTime(10, null, { leading: false, trailing: true }),
      takeUntil(this._destroy$)
    ).subscribe((selectedTemplate) => {
      if (!selectedTemplate) return;

      if (selectedTemplate.id) {
        this._changeTemplate(selectedTemplate);
      } else if (Object.keys(selectedTemplate).length === 0) {
        this._createNewTemplate();
      }
    });
  }
  
  private _setupLoadManualVariablesTrigger(): void {
    const textareas = this.textareas.toArray();
    if (!textareas?.length) return;

    merge(
      textareas[0].options.control.valueChanges,
      textareas[1].options.control.valueChanges,
      textareas[2].options.control.valueChanges
    ).pipe(
      map(() => [
          textareas[0].getUsedVariables(),
          textareas[1].getUsedVariables(),
          textareas[2].getUsedVariables()
      ]),
      takeUntil(this._destroy$)
    ).subscribe(([summary, name, content]) => {
      this._loadManualVariables(new Set([...name, ...content, ...summary]));
    });
  }

  private _onTemplateChangeSetModifiedBy(): void {
    const textareas = this.textareas.toArray();
    if (!textareas?.length) return;

    merge(
      textareas[0].options.control.valueChanges,
      textareas[1].options.control.valueChanges,
      textareas[2].options.control.valueChanges,
      this.previewForm.controls.name.valueChanges
    ).pipe(
      take(1)
    ).subscribe(() => {
      this.previewForm.controls.modifiedBy.setValue(this._authService.user?.fullName);
    });
  }

  private _mapToRequestObject(): TrafficDeviationInformationTemplateRequest {
    const formValues = this.previewForm.getRawValue();
    const informationTemplate: TrafficDeviationInformationTemplateRequest = {
      id: formValues.id,
      version: formValues.version,
      name: formValues.name,
      summary: this._convertChipsToHashedText(formValues.summary),
      header: this._convertChipsToHashedText(formValues.header),
      content: this._convertChipsToHashedText(formValues.content),
      modifiedBy: formValues.modifiedBy,
      automaticTemplate: formValues.automaticTemplate,
      automaticDescription: formValues.automaticDescription,
      deviationTypes: Object.keys(formValues.deviationTypes).filter((key) => {
        return this.previewForm.controls.deviationTypes.value[key];
      })
    };

    return informationTemplate;
  }

  private _convertChipsToHashedText(innerHTML: string): string {
    const translations = this._translateStore.getTranslations(this._translateService.currentLang) as Record<string, unknown>;
    const translationValues = Object.values(translations);
    const translationKeys = Object.keys(translations);

    const regExpTranslateChipText = (regexp: RegExp, text: string): string => {
      return text?.replaceAll(regexp, (match, p1) => {
        const translationIndex = translationValues.findIndex((k) => k === p1);
        if (translationIndex === -1) return match;
        return `#${translationKeys.at(translationIndex)}#`;
      });
    };    

    let text = regExpTranslateChipText(/#([^#]+)#/g, innerHTML);
    text = regExpTranslateChipText(/<span[^>]*>(.*?)<\/span>/g, text);
    return text;
  }

  private _getTrafficDeviationInformationTemplates(): void {
    this._taService.getTrafficDeviationInformationTemplates().pipe(
      catchError(error => {
          handleError(error);
          return of(undefined);
      })
    ).subscribe((informationTemplates: InformationTemplate[]) => {
        if (informationTemplates) {
          this._trafficDeviationsStore.updateTrafficDeviationState({ informationTemplates });
          this._trafficDeviationsStore.informationTemplates$.pipe(
            take(1)
          ).subscribe((templates) => {
            const template = templates.find((t) => t.id === this.selectedTemplateId);
            this.selectedTemplateId = undefined;
            if (!template) return;

            this.selectedTemplate$.next(template);
          });
        }
    });
  }

  private _publishTemplateMessage(
    message: string, 
    backgroundCritical = false, 
    milliseconds = 3000
  ): void {
    if (backgroundCritical) this.templateMessageBackgroundCritical = true;
    this.templateMessage$.next(message);

    timer(milliseconds).pipe(take(1)).subscribe(() => {
      this.templateMessage$.next('');
      this.templateMessageBackgroundCritical = false;
    });
  }

  private _deleteTemplate(): void {
    this._trafficDeviationsStore.informationTemplates$.pipe(
      take(1)
    ).subscribe((informationTemplates) => {
      if (!informationTemplates?.length) return;

      const informationTemplateIndex = informationTemplates.findIndex((template) => {
        return template.id === this.selectedTemplateId;
      });

      if (informationTemplateIndex !== -1) {
        this._taService.deleteTrafficDeviationInformationTemplates(
          this.selectedTemplateId
        ).pipe(
          catchError(error => {
              handleError(error);
              return of(undefined);
          })
        ).subscribe(() => {
          const informationTemplate = informationTemplates[informationTemplateIndex];
          const translation = this._translateService.instant('Template: {{name}}, deleted', { 
            name: informationTemplate.name 
          });
          this._publishTemplateMessage(translation, true);

          informationTemplates.splice(informationTemplateIndex, 1);
          this._trafficDeviationsStore.updateTrafficDeviationState({ informationTemplates });

          if (informationTemplates.length) {
            this._selectFirstManualTemplate(informationTemplates);
            return;
          }

          this.selectedTemplate$.next({} as InformationTemplate);
        });

        return;
      }

      this._selectFirstManualTemplate(informationTemplates);
    });
  }

  private _selectFirstManualTemplate(informationTemplates: InformationTemplate[]): void {
      const manualTemplates = informationTemplates.filter((template) => {
        return !template.automaticTemplate;
      });
      const template = manualTemplates?.length && manualTemplates[0] || {} as InformationTemplate;
      this.selectedTemplate$.next(template);
  }
}